// cards-dto-processor.ts

import { ICardExtended, CARD_TYPE, CARD_PURCHASE_STATUS, ICard } from '../../../models/card';
import { DtoICard } from '../../../models/collection';

export interface CardProcessingOptions {
  parentCollectionId?: string;
  resolveWithDownloadLink?: boolean;
}

export function processCardsDtoUnified(cardsDto: DtoICard[], options: CardProcessingOptions = {}): ICardExtended[] {
  // Ensure input is an array
  if (!Array.isArray(cardsDto)) {
    // Either throw an error or convert to [cardsDto]
    // throw new Error('cardsDto is not an array');
    cardsDto = [cardsDto] as DtoICard[];
  }

  // Filter out null/undefined items before processing
  return cardsDto
    .filter(dto => dto != null)
    .map(dto => processCardDtoUnified(dto, options));
}

export function processCardDtoUnified(dtoCard: DtoICard, options: CardProcessingOptions): ICardExtended {
  // Add additional check in case dto is still undefined/null
  if (!dtoCard) {
    console.error('[CardsDtoProcessor] Received null/undefined dtoCard');
    throw new Error('Cannot process null or undefined card DTO');
  }

  const { parentCollectionId } = options;
  const resolvedModifiedDate = resolveCardModifiedDate(dtoCard);

  const baseCard: ICardExtended = {
    id: dtoCard.model_id || dtoCard.original_id,
    reviewId: dtoCard.review_id,
    cardSharingId: dtoCard.share_id,
    parentCollectionId,
    publicModelId: dtoCard.public_model_id,
    modifiedDate: resolvedModifiedDate,
    cardContentMobile: dtoCard.preview_link_app || '',
    cardPosterMobile: dtoCard.poster_link_app || '',
    cardPosterWeb: dtoCard.poster_link_web || '',
    cardContentWeb: dtoCard.preview_link_web || '',
    cardCategory: dtoCard.card_category || '',
    cardType: dtoCard.type === 'collection' ? CARD_TYPE.COLLECTION : CARD_TYPE.MODEL,
    // NOTE: Using owner_id should be deprecated soon !!!
    cardAuthor: dtoCard.owner_id || dtoCard.user_id,
    cardTitle: dtoCard.title || '',
    cardDescription: dtoCard.description || '',
    cardPrice: processCardPrice(dtoCard.price),
    purchaseStatus: (dtoCard.review_status || dtoCard.model_review_status) ? processCardStatus(dtoCard.review_status || dtoCard.model_review_status) : setCustomStatus(dtoCard.status, dtoCard.user_id),
    reviewDetails: dtoCard.review_details,
    isShared: dtoCard.is_shared,
    views: dtoCard.views,
    reviewCreatedAt: dtoCard.review_created_at || '',
    rejectionViewed: Boolean(dtoCard.rejection_viewed),
    rejectionViewedAt: dtoCard.rejection_viewed_at ?? null,
  };

  if(options?.resolveWithDownloadLink) baseCard.cardModelLink = dtoCard.download_link || '';

  return baseCard;
}

function resolveCardModifiedDate(dtoCard: DtoICard): string {
  const candidateDates = [
    dtoCard.date_modified,
    dtoCard.last_update,
    dtoCard.creation_date,
    dtoCard.review_created_at,
  ];

  const firstValidDate = candidateDates.find((candidateDate) => typeof candidateDate === 'string' && candidateDate.trim().length > 0);
  return firstValidDate || '';
}

export function processCardStatus(status?: string): CARD_PURCHASE_STATUS {
  switch (status) {
    case 'purchased': return CARD_PURCHASE_STATUS.PURCHASED;
    case 'uploaded':  return CARD_PURCHASE_STATUS.UPLOADED;
    case 'liked':     return CARD_PURCHASE_STATUS.DRAFT_LIKED;
    case 'in_review': return CARD_PURCHASE_STATUS.IN_REVIEW;
    case 'rejected':  return CARD_PURCHASE_STATUS.REJECTED;
    case 'published': return CARD_PURCHASE_STATUS.PUBLISHED;
    case 'review_publish_reject': return CARD_PURCHASE_STATUS.REVIEW_PUBLISH_REJECT;
    default: return CARD_PURCHASE_STATUS.DEFAULT_3DWAY;
  }
}

export function processCardPrice(price: string | number | undefined): number | undefined {
  if (typeof price === 'string' && /^\d+$/.test(price)) {
    return parseInt(price, 10);
  } else if (typeof price === 'number') {
    return price;
  }
  return undefined;
}

export function convertCardsToDtoCards(cards: ICard[]): DtoICard[] {
  return cards.map(cardOriginal => {
    return {
      model_id: cardOriginal.id,
      last_update: cardOriginal.modifiedDate ? convertDateToISO8601(cardOriginal.modifiedDate) : new Date().toISOString(),
      public_model_id: cardOriginal.publicModelId,
      collectionId: cardOriginal.parentCollectionId,
      preview_link_app: cardOriginal.cardContentMobile,
      poster_link_app: cardOriginal.cardPosterMobile,
      poster_link_web: cardOriginal.cardPosterWeb,
      preview_link_web: cardOriginal.cardContentWeb,
      user_id: cardOriginal.cardAuthor,
      card_category: cardOriginal.cardCategory,
      card_subcategory: 'N/A',
      download_link: cardOriginal.cardModelLink,
      title: cardOriginal.cardTitle,
      description: cardOriginal.cardDescription,
      price: processCardPrice(cardOriginal.cardPrice),
      type: cardOriginal.cardType === CARD_TYPE.COLLECTION ? 'collection' : 'model',
      previewType: cardOriginal.cardType,
      status: cardOriginal.purchaseStatus,
      review_status: cardOriginal.purchaseStatus
    };
  });
}

// TEMPORARY WORKAROUND: Setting 'UPLOADED' status for authors until we finalize status definitions
function setCustomStatus(status: string, user: string): CARD_PURCHASE_STATUS {
  let storedUid: string | null = null;
  try { storedUid = localStorage.getItem('originalUid') } catch (error) { }
  return (status === 'private' && storedUid === user)
    ? CARD_PURCHASE_STATUS.UPLOADED
    : CARD_PURCHASE_STATUS.DEFAULT_3DWAY;
}

function convertDateToISO8601(date: string): string {
  const dateObj = new Date(date);
  return dateObj.toISOString();
}
